import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Container, Card, Form, Button, Row, Col, Alert } from "react-bootstrap";
import { API_URL } from "..";
import swal from "sweetalert";


const EditPayrollAdvance = () => {
  const { emp_id, month, year } = useParams();
  const [payrollData, setPayrollData] = useState(null);
  const [formData, setFormData] = useState({
    salary_amount: 0,
    allowance: 0,
    advance: 0,
    loan: 0,
    tax_amount: 0,
    bonuses: 0,
    commission: 0,
    deductions: 0,
    fine: 0,
    gross_salary: 0,
    net_salary: 0,
    status: "pending"
  });
  const [alert, setAlert] = useState({ show: false, variant: "", message: "" });

  useEffect(() => {
    fetchPayrollData();
  }, [emp_id]);

  const fetchPayrollData = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const response = await fetch(`${API_URL}/api/payroll/generate-single`, {
        method: "POST",
        body:JSON.stringify({
            emp_id:emp_id,
            month:month,
            year: year
        }),
        headers: { "Accept": "*/*",'Content-Type':'application/json' }
      });
      const data = await response.json();
      if (response.status === 200) {
        setPayrollData(data.payroll);
        setFormData({
          salary_amount: data.payroll.salary_amount || 0,
          allowance: data.payroll.allowance || 0,
          advance: data.payroll.advance || 0,
          loan: data.payroll.loan || 0,
          tax_amount: 0,
          bonuses: 0,
          commission: 0,
          deductions: data.payroll.deducted_salary || 0,
          fine: 0,
          gross_salary: parseFloat(data.payroll.gross_salary) || 0,
          net_salary: parseFloat(data.payroll.net_salary) || 0,
          status: data.payroll.status || "pending"
        });
      } else {
        setAlert({ show: true, variant: "danger", message: "Failed to fetch payroll data" });
      }
    } catch (error) {
      setAlert({ show: true, variant: "danger", message: "Error fetching payroll data" });
    }
  };

  const calculateSalaries = (updatedFormData) => {
    const gross_salary =
      parseFloat(updatedFormData.salary_amount || 0) +
      parseFloat(updatedFormData.allowance || 0) +
      parseFloat(updatedFormData.bonuses || 0) +
      parseFloat(updatedFormData.commission || 0);

    const totalDeductions =
      parseFloat(updatedFormData.advance || 0) +
      parseFloat(updatedFormData.loan || 0) +
      parseFloat(updatedFormData.tax_amount || 0) +
      parseFloat(updatedFormData.deductions || 0) +
      parseFloat(updatedFormData.fine || 0);

    const net_salary = gross_salary - totalDeductions;

    return { gross_salary, net_salary };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    const { gross_salary, net_salary } = calculateSalaries(updatedFormData);
    setFormData({ ...updatedFormData, gross_salary, net_salary });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const response = await fetch(`${API_URL}/api/payroll/update/${emp_id}`, {
        method: "POST",
        headers: {
          "Accept": "*/*",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          org_id: user.id,
          ...formData
        })
      });
      if (response.status === 200) {
        setAlert({ show: true, variant: "success", message: "Payroll updated successfully" });
      } else {
        setAlert({ show: true, variant: "danger", message: "Failed to update payroll" });
      }
    } catch (error) {
      setAlert({ show: true, variant: "danger", message: "Error updating payroll" });
    }
  };

  const handleApprove = async () => {
    const result = await swal({
      title: "Approve Payroll",
      text: "Are you sure you want to approve this payroll? It will be locked for further edits.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      setFormData({ ...formData, status: "approved" });
      handleSubmit({ preventDefault: () => {} }); // Trigger save with updated status
      swal("Approved!", "Payroll has been approved and locked.", "success");
    }
  };

  const handleUnlock = async () => {
    const result = await swal({
      title: "Unlock Payroll",
      text: "Are you sure you want to unlock this payroll for editing?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Unlock",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      setFormData({ ...formData, status: "pending" });
      handleSubmit({ preventDefault: () => {} }); // Trigger save with updated status
      swal("Unlocked!", "Payroll has been unlocked for editing.", "success");
    }
  };

  if (!payrollData) return <div>Loading...</div>;

  return (
    <MasterLayout>
      <Breadcrumb title="Edit Payroll" />
      <Container className="py-4">
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Card.Title className="mb-4">Edit Payroll for {payrollData.name}</Card.Title>
            {alert.show && (
              <Alert variant={alert.variant} onClose={() => setAlert({ ...alert, show: false })} dismissible>
                {alert.message}
              </Alert>
            )}
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Basic Salary</Form.Label>
                    <Form.Control
                      type="number"
                      name="salary_amount"
                      value={formData.salary_amount}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Allowance</Form.Label>
                    <Form.Control
                      type="number"
                      name="allowance"
                      value={formData.allowance}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bonuses</Form.Label>
                    <Form.Control
                      type="number"
                      name="bonuses"
                      value={formData.bonuses}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Commission</Form.Label>
                    <Form.Control
                      type="number"
                      name="commission"
                      value={formData.commission}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Advance</Form.Label>
                    <Form.Control
                      type="number"
                      name="advance"
                      value={formData.advance}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Loan</Form.Label>
                    <Form.Control
                      type="number"
                      name="loan"
                      value={formData.loan}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tax Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="tax_amount"
                      value={formData.tax_amount}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Deductions</Form.Label>
                    <Form.Control
                      type="number"
                      name="deductions"
                      value={formData.deductions}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fine</Form.Label>
                    <Form.Control
                      type="number"
                      name="fine"
                      value={formData.fine}
                      onChange={handleInputChange}
                      disabled={formData.status === "approved"}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gross Salary</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.gross_salary.toFixed(2)}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Net Salary</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.net_salary.toFixed(2)}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex gap-3 mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={formData.status === "approved"}
                >
                  Save Changes
                </Button>
                {formData.status === "pending" ? (
                  <Button variant="success" onClick={handleApprove}>
                    Approve Payroll
                  </Button>
                ) : (
                  <Button variant="warning" onClick={handleUnlock}>
                    Unlock Payroll
                  </Button>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </MasterLayout>
  );
};

export default EditPayrollAdvance;